import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaClock, FaCrown, FaCheckCircle, FaSignOutAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { TRIAL_CONFIG } from '../constants';

const TrialExpiredModal = () => {
  const { showTrialExpiredModal, dismissTrialExpiredModal, handleTrialExpired, trialStatus, logout } = useAuth();

  if (!showTrialExpiredModal || !trialStatus) return null;

  return (
    <Modal 
      show={showTrialExpiredModal} 
      onHide={dismissTrialExpiredModal}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
      dialogClassName="trial-expired-modal"
    >
      <style>{`
        .trial-expired-modal .modal-content {
          border: none;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          overflow: hidden;
        }
        .trial-expired-modal .modal-header {
          border: none;
          background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
          color: white;
          padding: 24px 32px 20px;
        }
        .trial-expired-modal .modal-body {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          padding: 32px;
        }
        .trial-expired-modal .modal-footer {
          border: none;
          background: #ffffff;
          padding: 24px 32px 32px;
        }
        .feature-item {
          transition: transform 0.2s ease-in-out;
        }
        .feature-item:hover {
          transform: translateX(5px);
        }
        .upgrade-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2) !important;
        }
        .logout-button:hover {
          transform: translateY(-1px);
        }
      `}</style>
      
      <Modal.Header>
        <Modal.Title style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          fontSize: '24px',
          fontWeight: '700',
          letterSpacing: '0.5px'
        }}>
          <FaClock style={{ 
            fontSize: '28px',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
          }} />
          Trial Period Expired
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div style={{ textAlign: 'center' }}>
          {/* Hero Icon */}
          <div style={{ 
            background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 18px',
            boxShadow: '0 8px 25px rgba(253, 203, 110, 0.3)',
            position: 'relative'
          }}>
            <FaClock style={{ 
              fontSize: '40px', 
              color: '#e17055',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
            }} />
            <div style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              background: '#e17055',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              boxShadow: '0 2px 8px rgba(225, 112, 85, 0.4)'
            }}>
              !
            </div>
          </div>
          
          {/* Main Message */}
          <h4 style={{ 
            marginBottom: '16px', 
            color: '#2d3436',
            fontWeight: '600',
            fontSize: '22px',
            letterSpacing: '0.3px'
          }}>
            Your {TRIAL_CONFIG.DURATION_DAYS}-day free trial has ended
          </h4>
          
          <p style={{ 
            color: '#636e72', 
            marginBottom: '32px',
            fontSize: '16px',
            lineHeight: '1.6',
            maxWidth: '400px',
            margin: '0 auto 32px'
          }}>
            Continue enjoying all the powerful features of SmartClose by choosing a subscription plan.
          </p>
          
          {/* Premium Features Card */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: '2px solid #e9ecef',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '18px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Premium Badge */}
            <div style={{
              position: 'absolute',
              top: '-10px',
              right: '20px',
              background: 'linear-gradient(135deg, #fd79a8 0%, #e84393 100%)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '0 0 12px 12px',
              fontSize: '12px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: '0 4px 12px rgba(232, 67, 147, 0.3)'
            }}>
              <FaCrown style={{ marginRight: '4px', fontSize: '10px' }} />
              Premium
            </div>
            
            <h6 style={{ 
              margin: '0 0 20px 0', 
              color: '#2d3436',
              fontSize: '20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <FaCheckCircle style={{ color: '#00b894', fontSize: '20px' }} />
              Unlock Premium Features
            </h6>
            
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '8px',
              textAlign: 'left'
            }}>
              {[
                { icon: '🚀', text: 'Full access to all features' },
                { icon: '♾️', text: 'Unlimited transactions' },
                { icon: '🎯', text: 'Priority customer support' },
                { icon: '🔗', text: 'Advanced integrations' },
                { icon: '👥', text: 'Team collaboration tools' },
                { icon: '📊', text: 'Advanced analytics & reporting' }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="feature-item"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 0',
                    color: '#636e72',
                    fontSize: '15px',
                    fontWeight: '500'
                  }}
                >
                  <span style={{ 
                    fontSize: '18px',
                    width: '24px',
                    textAlign: 'center'
                  }}>
                    {feature.icon}
                  </span>
                  {feature.text}
                </div>
              ))}
            </div>
          </div>
          
          {/* Call to Action */}
          <p style={{
            color: '#74b9ff',
            fontSize: '14px',
            fontWeight: '600',
            margin: '0 0 -8px 0',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Choose your plan to continue →
          </p>
        </div>
      </Modal.Body>
      
      <Modal.Footer style={{ 
        justifyContent: 'center', 
        gap: '16px',
        flexWrap: 'wrap',
        paddingTop: '16px'
      }}>
        <Button 
          variant="outline-secondary"
          onClick={logout}
          className="logout-button"
          style={{ 
            minWidth: '140px',
            padding: '12px 24px',
            border: '2px solid #ddd',
            borderRadius: '12px',
            fontWeight: '600',
            fontSize: '14px',
            color: '#636e72',
            background: 'transparent',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          onMouseOver={e => {
            e.currentTarget.style.borderColor = '#636e72';
            e.currentTarget.style.color = '#2d3436';
          }}
          onMouseOut={e => {
            e.currentTarget.style.borderColor = '#ddd';
            e.currentTarget.style.color = '#636e72';
          }}
        >
          <FaSignOutAlt style={{ fontSize: '14px' }} />
          Logout
        </Button>
        
        <Button 
          onClick={handleTrialExpired}
          className="upgrade-button"
          style={{ 
            minWidth: '160px',
            padding: '12px 28px',
            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
            border: 'none',
            borderRadius: '12px',
            fontWeight: '700',
            fontSize: '16px',
            color: 'white',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            boxShadow: '0 6px 20px rgba(116, 185, 255, 0.4)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}
        >
          <FaCrown style={{ fontSize: '16px' }} />
          Choose Plan
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TrialExpiredModal;